import { InputThrust, InputRotateLeft, InputRotateRight, encodeInput } from '../wire/input.js'
import { GameMode } from '../mode/state.js'

// KeyboardInput tracks held keys and fires increments on key-down.
// Each frame, call snapshot() to produce the current InputPacket.
//
// Iteration 1.12: synthesizeFire() and overrideOnce() let server-pushed
// `kind:"test.*"` frames poke the input layer directly. Both are no-ops
// in normal play (nothing calls them) and don't affect normal keyboard
// behavior.
//
// Iteration 1.14: setGameMode() gates snapshot(). Outside of arena, every
// snapshot returns all-zeroes regardless of held keys, so lobby clicks /
// keystrokes never produce gameplay input on the wire.
export class KeyboardInput {
  constructor() {
    this.held = new Set()
    this.fireCount = 0
    this.activateCount = 0
    this._override = null
    this._mode = GameMode.ARENA_LOBBY
  }

  setGameMode(mode) {
    this._mode = mode
  }

  start() {
    window.addEventListener('keydown', (e) => {
      if (e.repeat) return
      const k = normalize(e.code)
      if (k) this.held.add(k)
      if (k === 'fire') this.fireCount = (this.fireCount + 1) & 0xffff
      if (k === 'activate') this.activateCount = (this.activateCount + 1) & 0xffff
      if (k) e.preventDefault()
    })
    window.addEventListener('keyup', (e) => {
      const k = normalize(e.code)
      if (k) this.held.delete(k)
    })
  }

  snapshot() {
    if (this._mode !== GameMode.ARENA) {
      return { flags: 0, fireCount: 0, activateCount: 0 }
    }
    let flags = 0
    if (this.held.has('thrust')) flags |= InputThrust
    if (this.held.has('rotate_left')) flags |= InputRotateLeft
    if (this.held.has('rotate_right')) flags |= InputRotateRight
    if (this._override !== null) {
      flags |= this._override.flags | 0
      this.fireCount = (this.fireCount + (this._override.fireDelta | 0)) & 0xffff
      this.activateCount = (this.activateCount + (this._override.activateDelta | 0)) & 0xffff
      this._override = null
    }
    return { flags, fireCount: this.fireCount, activateCount: this.activateCount }
  }

  encodedSnapshot() {
    return encodeInput(this.snapshot())
  }

  // Test hook: bump fireCount as if the user pressed the fire key n times.
  // Counter wrap is the same 16-bit wrap normal key presses use.
  synthesizeFire(n) {
    const k = Math.max(0, n | 0)
    this.fireCount = (this.fireCount + k) & 0xffff
  }

  // Test hook: clobber the next snapshot's flags / counter deltas. Cleared
  // after one frame.
  overrideOnce({ flags = 0, fireDelta = 0, activateDelta = 0 } = {}) {
    this._override = { flags, fireDelta, activateDelta }
  }
}

function normalize(code) {
  switch (code) {
    case 'KeyW':
    case 'ArrowUp':
      return 'thrust'
    case 'KeyA':
    case 'ArrowLeft':
      return 'rotate_left'
    case 'KeyD':
    case 'ArrowRight':
      return 'rotate_right'
    case 'Space':
      return 'fire'
    case 'KeyE':
      return 'activate'
    default:
      return null
  }
}
